import { useT } from "@transifex/react";
import type { Moment } from "moment";
import type { SchoolClass } from "p6m-p6u";
import BasePopupWrapper, { TitleColorVariants } from "../../basic/popups/BasePopupWrapper";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { NO_CLASS_SELECTION, ShareModes, ShareSteps, ShareTimings } from "../../enums/shareFlow";
import { allowsNoClassSelection, hasEffectivePrivateClass, requiresPrivateClass } from "../../helpers/ShareFlow";
import { useNow } from "../../hooks/useNow";
import { isPrivateSchoolClass, validateClassName } from "../../utils/schoolClassUtils";
import type { NewClassFormState } from "../classActivityCard/ClassActivityCardsOverview.types";
import { ButtonColorVariant, IconPosition } from "../teacherArea/AreaButton";
import ShareClassStep from "./steps/ShareClassStep";
import ShareDetailsStep from "./steps/ShareDetailsStep";
import ShareModeStep from "./steps/ShareModeStep";
import ShareTimingStep from "./steps/ShareTimingStep";

interface ShareFlowPopupCommonProps {
    isOpen: boolean;
    onClose: () => void;

    step: ShareSteps;
    onPrimaryAction: () => void;
    onPrevStep: () => void;
    isFinalAction: boolean;
    shareInProgress: boolean;

    // Class step
    selectedSchoolClassId: string | null;
    onSelectClass: (schoolClassId: string | null) => void;
    schoolClasses: SchoolClass[];
    newClassForm: NewClassFormState | null;
    onOpenNewClassForm: () => void;
    onCloseNewClassForm: () => void;
    onCollapseNewClassForm: () => void;
    onNewClassNameChange: (name: string) => void;
    onToggleNewClassConsent: () => void;

    // Timing step
    selectedTiming: ShareTimings;
    onSelectTiming: (timing: ShareTimings) => void;
    visibilityDateValue: Moment | null;
    onVisibilityDateChange: (value: Moment | null) => void;
}

export interface ShareFlowPopupTestProps extends ShareFlowPopupCommonProps {
    classActivityCategory: ClassActivityCategory.TEST;

    selectedMode: ShareModes | null;
    onSelectMode: (shareMode: ShareModes) => void;
    testName: string;
    infoExpanded: boolean;
    onToggleInfo: () => void;
    // Details step
    message: string;
    onMessageChange: (message: string) => void;
    showDeadline: boolean;
    onToggleDeadline: () => void;
    deadlineValue: Moment | null;
    onDeadlineChange: (value: Moment | null) => void;
    showDuration: boolean;
    onToggleDuration: () => void;
    durationHours: string;
    durationMinutes: string;
    onDurationHoursSelect: (value: number) => void;
    onDurationMinutesSelect: (value: number) => void;
}

export interface ShareFlowPopupVocabProps extends ShareFlowPopupCommonProps {
    classActivityCategory: ClassActivityCategory.VOCAB_TRAINING;
}

type ShareFlowPopupProps = ShareFlowPopupTestProps | ShareFlowPopupVocabProps;

// Test flow walks four steps; vocab flow only needs class + timing.
const STEPS_BY_CATEGORY: Record<ClassActivityCategory, ShareSteps[]> = {
    [ClassActivityCategory.TEST]: [ShareSteps.MODE, ShareSteps.CLASS, ShareSteps.TIMING, ShareSteps.DETAILS],
    [ClassActivityCategory.VOCAB_TRAINING]: [ShareSteps.CLASS, ShareSteps.TIMING],
};

interface CategoryStyles {
    title: string;
    titleColor: TitleColorVariants;
    buttonColor: ButtonColorVariant;
}

const ShareFlowPopup = (props: ShareFlowPopupProps) => {
    const t = useT();
    // Re-render each minute so the timing step's past-date info notice and the details step's
    // deadline bounds track wall-clock time while the teacher dwells in the popup.
    useNow();

    const t_titleTest = t("Share Online Task", { _tags: "ShareFlow, ShareFlowPopup" });
    const t_titleVocab = t("Share vocabulary training", { _tags: "ShareFlow, ShareFlowPopup" });
    const t_cancel = t("Cancel", { _tags: "ShareFlow, ShareFlowPopup" });
    const t_back = t("Back", { _tags: "ShareFlow, ShareFlowPopup" });
    const t_next = t("Next", { _tags: "ShareFlow, ShareFlowPopup" });
    const t_share = t("Share", { _tags: "ShareFlow, ShareFlowPopup" });
    const t_testOnlyPrivate = t("Tests are only possible in private classes.", {
        _tags: "ShareFlow, ShareClassStep, ShareModeStep",
    });
    const t_infoLine1 = t("Students log in to private classes with a personal code.", {
        _tags: "ShareFlow, ShareClassStep",
    });
    const t_infoLine2 = t("This allows their answers to be saved and results shared.", {
        _tags: "ShareFlow, ShareClassStep",
    });
    const t_timingQuestionTest = t("When should this test be shared?", { _tags: "ShareFlow, ShareTimingStep" });
    const t_timingQuestionPractice = t("When should this practice be shared?", {
        _tags: "ShareFlow, ShareTimingStep",
    });
    const t_timingQuestionVocab = t("When should this vocabulary training be shared?", {
        _tags: "ShareFlow, ShareTimingStep",
    });
    const t_nowDescriptionTest = t("The test will be visible immediately.", { _tags: "ShareFlow, ShareTimingStep" });
    const t_nowDescriptionPractice = t("The practice will be visible immediately.", {
        _tags: "ShareFlow, ShareTimingStep",
    });
    const t_nowDescriptionVocab = t("The vocabulary training will be visible immediately.", {
        _tags: "ShareFlow, ShareTimingStep",
    });

    const categoryStyles: Record<ClassActivityCategory, CategoryStyles> = {
        [ClassActivityCategory.TEST]: {
            title: t_titleTest,
            titleColor: TitleColorVariants.DEFAULT,
            buttonColor: ButtonColorVariant.DEFAULT,
        },
        [ClassActivityCategory.VOCAB_TRAINING]: {
            title: t_titleVocab,
            titleColor: TitleColorVariants.SECONDARY,
            buttonColor: ButtonColorVariant.SECONDARY,
        },
    };
    const styles = categoryStyles[props.classActivityCategory];
    const steps = STEPS_BY_CATEGORY[props.classActivityCategory];

    const isFirstStep = props.step === steps[0];

    const isVocabFlow = props.classActivityCategory === ClassActivityCategory.VOCAB_TRAINING;
    const isPracticeMode = !isVocabFlow && props.selectedMode === ShareModes.PRACTICE;
    const isTestMode = !isVocabFlow && props.selectedMode === ShareModes.TEST;

    // Keyed off the selected class, not the current mode, so a class assigned outside the flow disables the
    // TEST card up front — and the same rule covers returning to this step after picking one in the class step.
    const isNoClassSelected = props.selectedSchoolClassId === NO_CLASS_SELECTION;
    const isPublicClassSelected =
        props.schoolClasses.find((c) => c.token === props.selectedSchoolClassId)?.classVisibility ===
        SchoolClassVisibilities.PUBLIC;
    const isTestDisabled = !isVocabFlow && (isPublicClassSelected || isNoClassSelected);

    const getTimingTexts = () => {
        if (isVocabFlow) return { question: t_timingQuestionVocab, nowDescription: t_nowDescriptionVocab };
        if (isPracticeMode) return { question: t_timingQuestionPractice, nowDescription: t_nowDescriptionPractice };
        return { question: t_timingQuestionTest, nowDescription: t_nowDescriptionTest };
    };

    const getIsPrimaryDisabled = () => {
        switch (props.step) {
            case ShareSteps.MODE:
                return isVocabFlow ? false : !props.selectedMode;
            case ShareSteps.CLASS: {
                if (props.newClassForm?.isOpen) {
                    const name = props.newClassForm.name.trim();
                    const existingNames = props.schoolClasses.map((c) => c.name);
                    const hasValidationError = !name || validateClassName(name, existingNames) !== null;
                    return hasValidationError || !props.newClassForm.hasConsent;
                }
                // Vocab flow allows "Keine Klasse + Weiter"; the hook's onPrimaryAction decides whether to
                // advance to TIMING or short-circuit to immediate share.
                if (isVocabFlow && props.selectedSchoolClassId === NO_CLASS_SELECTION) return false;
                // Test-mode shares positively require a private class: a stale public/unknown token (class
                // list still loading or failed to load) must not enable Next.
                if (requiresPrivateClass(props.classActivityCategory, isVocabFlow ? null : props.selectedMode)) {
                    return !hasEffectivePrivateClass(
                        props.selectedSchoolClassId,
                        props.newClassForm,
                        props.schoolClasses
                    );
                }
                return props.selectedSchoolClassId === null && !props.newClassForm?.isCommitted;
            }
            case ShareSteps.TIMING:
                // A past moment is allowed — the activity shares immediately and the timing step says
                // so via an info notice — so only a missing date blocks the primary action.
                return props.selectedTiming === ShareTimings.LATER && !props.visibilityDateValue;
            case ShareSteps.DETAILS:
                // Student message is optional; a past planned date is never blocked — it is clamped on submit.
                return false;
            default:
                return false;
        }
    };

    const handlePrimaryClick = () => {
        props.onPrimaryAction();
    };

    const handleSecondaryClick = () => {
        if (isFirstStep) {
            props.onClose();
        } else {
            props.onPrevStep();
        }
    };

    // ESC and overlay click bypass the disabled secondary button, so guard the modal-level close too.
    const handleClose = () => {
        if (props.shareInProgress) return;
        props.onClose();
    };

    const renderModeStep = () => {
        if (props.classActivityCategory !== ClassActivityCategory.TEST) return null;
        return (
            <ShareModeStep
                selectedMode={props.selectedMode}
                onSelectMode={props.onSelectMode}
                isTestDisabled={isTestDisabled}
                isNoClassSelected={isNoClassSelected}
                testName={props.testName}
                classActivityCategory={props.classActivityCategory}
            />
        );
    };

    const renderClassStep = () => (
        <ShareClassStep
            selectedSchoolClassId={props.selectedSchoolClassId}
            onSelectClass={props.onSelectClass}
            schoolClasses={props.schoolClasses}
            classActivityCategory={props.classActivityCategory}
            showNoClassCard={allowsNoClassSelection(
                props.classActivityCategory,
                isVocabFlow ? null : props.selectedMode
            )}
            isClassDisabled={isTestMode ? (cls) => !isPrivateSchoolClass(cls.classVisibility) : undefined}
            headerHint={
                isTestMode && props.classActivityCategory === ClassActivityCategory.TEST
                    ? {
                          text: t_testOnlyPrivate,
                          infoExpanded: props.infoExpanded,
                          onToggleInfo: props.onToggleInfo,
                          infoLines: [t_infoLine1, t_infoLine2],
                      }
                    : undefined
            }
            newClassForm={props.newClassForm}
            onOpenNewClassForm={props.onOpenNewClassForm}
            onCloseNewClassForm={props.onCloseNewClassForm}
            onCollapseNewClassForm={props.onCollapseNewClassForm}
            onNewClassNameChange={props.onNewClassNameChange}
            onToggleNewClassConsent={props.onToggleNewClassConsent}
        />
    );

    const renderTimingStep = () => {
        const texts = getTimingTexts();
        const deadlineValue =
            props.classActivityCategory === ClassActivityCategory.TEST && props.showDeadline
                ? props.deadlineValue
                : null;
        return (
            <ShareTimingStep
                questionText={texts.question}
                nowDescription={texts.nowDescription}
                selectedTiming={props.selectedTiming}
                onSelectTiming={props.onSelectTiming}
                visibilityDateValue={props.visibilityDateValue}
                onVisibilityDateChange={props.onVisibilityDateChange}
                deadlineValue={deadlineValue}
                classActivityCategory={props.classActivityCategory}
            />
        );
    };

    const renderDetailsStep = () => {
        if (props.classActivityCategory !== ClassActivityCategory.TEST) return null;
        return (
            <ShareDetailsStep
                selectedMode={props.selectedMode}
                message={props.message}
                onMessageChange={props.onMessageChange}
                showDeadline={props.showDeadline}
                onToggleDeadline={props.onToggleDeadline}
                deadlineValue={props.deadlineValue}
                onDeadlineChange={props.onDeadlineChange}
                visibilityDate={props.selectedTiming === ShareTimings.LATER ? props.visibilityDateValue : null}
                showDuration={props.showDuration}
                onToggleDuration={props.onToggleDuration}
                durationHours={props.durationHours}
                durationMinutes={props.durationMinutes}
                onDurationHoursSelect={props.onDurationHoursSelect}
                onDurationMinutesSelect={props.onDurationMinutesSelect}
                disabled={props.shareInProgress}
            />
        );
    };

    const renderStepContent = () => {
        switch (props.step) {
            case ShareSteps.MODE:
                return renderModeStep();
            case ShareSteps.CLASS:
                return renderClassStep();
            case ShareSteps.TIMING:
                return renderTimingStep();
            case ShareSteps.DETAILS:
                return renderDetailsStep();
            default:
                return null;
        }
    };

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={handleClose}
            title={styles.title}
            titleColor={styles.titleColor}
            buttonLayout="between"
            secondaryButton={{
                text: isFirstStep ? t_cancel : t_back,
                icon: isFirstStep ? undefined : "chevron-left",
                iconPosition: isFirstStep ? undefined : IconPosition.LEFT,
                onClick: handleSecondaryClick,
                disabled: props.shareInProgress,
                colorVariant: styles.buttonColor,
                className: "min-w-[180px]",
            }}
            primaryButton={{
                text: props.isFinalAction ? t_share : t_next,
                icon: props.isFinalAction ? "share" : "chevron-right",
                iconPosition: IconPosition.RIGHT,
                onClick: handlePrimaryClick,
                disabled: props.shareInProgress || getIsPrimaryDisabled(),
                colorVariant: styles.buttonColor,
                className: "min-w-[180px]",
            }}
        >
            {renderStepContent()}
        </BasePopupWrapper>
    );
};

export default ShareFlowPopup;
